import RequsetApI from '@/js/request/requsetApI';

const state = {
    // SSE 연결 상태
    connection: null,
    isConnected: false,
    isConnecting: false,
    
    // 수신된 메시지들
    messages: [],
    
    // 연결 정보
    connectionInfo: {
        url: null,
        lastConnected: null,
        lastHeartbeat: null  // 마지막 heartbeat 시간
    },
    
    // 에러 상태
    error: null,
    
    // 메시지 타입별 데이터
    notifications: [],
    systemAlerts: [],
    realTimeData: {},
    
    // 알림 초기 로드 완료 여부
    initialLoadCompleted: false,
    
    // API 호출 중 플래그 (중복 호출 방지)
    isLoadingNotifications: false,
    
    // 연결 유지 관련
    heartbeatInterval: null,      // heartbeat 체크 interval
    reconnectAttempts: 0,         // 재연결 시도 횟수
    maxReconnectAttempts: 5,      // 최대 재연결 시도 횟수
    reconnectDelay: 3000,         // 재연결 지연 시간 (3초)
    heartbeatTimeout: 10000       // heartbeat 타임아웃 (10초)
};

const getters = {
    isConnected: state => state.isConnected,
    isConnecting: state => state.isConnecting,
    messages: state => state.messages,
    notifications: state => state.notifications,
    systemAlerts: state => state.systemAlerts,
    realTimeData: state => state.realTimeData,
    connectionInfo: state => state.connectionInfo,
    error: state => state.error,
    
    // 읽지 않은 알림 개수
    unreadNotificationCount: state => {
        return state.notifications.filter(notification => !notification.read).length;
    },
    
    // 🔧 연결 가능 여부 확인 (중복 연결 방지)
    canConnect: state => {
        return !state.isConnected && !state.isConnecting && !state.connection;
    },
    
    // 🔧 연결 상태 요약
    connectionStatus: state => {
        if (state.isConnected) return 'connected';
        if (state.isConnecting) return 'connecting';
        if (state.error) return 'error';
        return 'disconnected';
    }
};

const mutations = {
    SET_CONNECTED(state, isConnected) {
        state.isConnected = isConnected;
        if (isConnected) {
            state.connectionInfo.lastConnected = new Date();
            state.error = null;
        }
    },
    
    SET_CONNECTING(state, isConnecting) {
        state.isConnecting = isConnecting;
    },
    
    SET_CONNECTION_URL(state, url) {
        state.connectionInfo.url = url;
    },
    
    SET_ERROR(state, error) {
        state.error = error;
        state.isConnected = false;
        state.isConnecting = false;
    },
    
    ADD_MESSAGE(state, message) {
        console.log('🔍 ADD_MESSAGE - Original message:', message);
        console.log('🔍 ADD_MESSAGE - message.data:', message.data);
        console.log('🔍 ADD_MESSAGE - message keys:', Object.keys(message));
        
        // 메시지를 messages 배열에 추가
        state.messages.push({
            ...message.data || message
        });
        
        // 최근 100개만 유지
        if (state.messages.length > 100) {
            state.messages = state.messages.slice(-100);
        }
        
        // 🔔 알림 관련 메시지인 경우 API에서 최신 데이터 다시 불러오기
        const messageData = message.data || message;
        console.log('🔍 ADD_MESSAGE - messageData after extraction:', messageData);
        
        // 🔍 알림 감지 조건 상세 로그
        console.log('🔍 Checking if message is notification:');
        console.log('   - messageData.alarmCd:', messageData.alarmCd);
        console.log('   - messageData.eventSeq:', messageData.eventSeq);
        console.log('   - messageData.eventCont:', messageData.eventCont);
        console.log('   - Condition result:', !!(messageData.alarmCd || messageData.eventSeq || messageData.eventCont));
        
        if (messageData.alarmCd || messageData.eventSeq || messageData.eventCont) {
            console.log('🔔 ✅ Message is a notification, refreshing notifications from API');
            
            // 🔄 API에서 최신 알림 데이터 불러오기 (비동기 처리)
            setTimeout(async () => {
                try {
                    console.log('📡 About to dispatch refresh-notifications-from-api event...');
                    console.log('📡 Window object available:', !!window);
                    console.log('📡 CustomEvent available:', !!CustomEvent);
                    
                    // loadNotifications action을 호출하기 위해 store context가 필요
                    // mutation에서는 action을 직접 호출할 수 없으므로 글로벌 이벤트로 처리
                    const event = new CustomEvent('refresh-notifications-from-api', {
                        detail: {
                            reason: 'SSE notification received',
                            messageData: messageData,
                            timestamp: new Date().toISOString()
                        }
                    });
                    
                    console.log('📡 Created custom event:', event);
                    window.dispatchEvent(event);
                    console.log('✅ refresh-notifications-from-api event dispatched successfully');
                } catch (error) {
                    console.error('❌ Failed to dispatch refresh event:', error);
                }
            }, 100);
        } else {
            console.log('🔔 ❌ Message is NOT a notification, skipping API refresh');
        }
    },
    
    SET_NOTIFICATIONS(state, notifications) {
        console.log('🔄 SET_NOTIFICATIONS called with:', notifications);
        console.log('🔄 Input notifications count:', notifications.length);
        
        if (notifications.length > 0) {
            console.log('🔍 First notification sample:', notifications[0]);
        }
        
        state.notifications = notifications.map((notification) => {
            console.log('🔍 SET_NOTIFICATIONS mapping notification:', notification);
            const mapped = {
                ...notification,
                // VO 객체 필드명 매핑
                id: notification.eventSeq || notification.id,
                read: notification.readYn === 'Y' || notification.isRead || notification.read || false,
                timestamp: new Date(notification.creDat || notification.createdAt || notification.createTime),
                title: notification.title || '알림',
                alarmNm: notification.alarmNm || '-',
                message: notification.eventCont || notification.content || notification.message,
                type: notification.alarmType || notification.type || 'info',
                // 추가 VO 필드들
                userId: notification.userId,
                regId: notification.refKey,
                updDate: notification.creDat,
                //updId: notification.updId
            };
            
            return mapped;
        });
        
        console.log('✅ Final state.notifications:', state.notifications);
        console.log('✅ Final notifications count:', state.notifications.length);
    },
    
    ADD_NOTIFICATION(state, notification) {

        console.log('🔔 ADD_NOTIFICATION: About to dispatch new-notification-received event', notification);
        const newNotification = {
            ...notification,
            read: false,
            readYn: 'N',  // API와 일치하는 필드 추가
            timestamp: new Date(),
            id: notification.eventSeq || notification.id || Date.now() + Math.random()
        };
        
        state.notifications.unshift(newNotification);
        
        // 최근 50개만 유지
        if (state.notifications.length > 50) {
            state.notifications = state.notifications.slice(0, 50);
        }
        
        // 새 알림 추가 시 전역 이벤트 발생 (SSENotificationUnit 업데이트용)
        console.log('🔔 ADD_NOTIFICATION: About to dispatch new-notification-received event', newNotification);
        console.log('🔔 Current notifications count:', state.notifications.length);
        
        setTimeout(() => {
            if (typeof window !== 'undefined') {
                const unreadCount = state.notifications.filter(n => !n.viewYn).length;
                console.log('🔔 Dispatching new-notification-received event', {
                    notification: newNotification,
                    unreadCount: unreadCount,
                    totalNotifications: state.notifications.length
                });
                
                window.dispatchEvent(new CustomEvent('new-notification-received', {
                    detail: {
                        notification: newNotification,
                        count: unreadCount
                    }
                }));
                console.log('🔔 Event dispatched successfully - listeners should be notified now');
            } else {
                console.warn('⚠️ Window is undefined, cannot dispatch event');
            }
        }, 100);
    },
    
    MARK_NOTIFICATION_READ(state, notificationId) {
        const notification = state.notifications.find(n => 
            n.id === notificationId || n.eventSeq === notificationId
        );
        if (notification) {
            notification.read = true;
            notification.readYn = 'Y';  // readYn도 함께 업데이트
            console.log('✅ Notification marked as read:', notification);
        } else {
            console.warn('⚠️ Notification not found for read marking:', notificationId);
        }
    },
    
    MARK_ALL_NOTIFICATIONS_READ(state) {
        state.notifications.forEach(notification => {
            notification.read = true;
            notification.readYn = 'Y';  // readYn도 함께 업데이트
        });
        console.log('✅ All notifications marked as read');
    },
    
    REMOVE_NOTIFICATION(state, notificationId) {
        state.notifications = state.notifications.filter(n => n.id !== notificationId);
    },
    
    ADD_SYSTEM_ALERT(state, alert) {
        state.systemAlerts.unshift({
            ...alert,
            timestamp: new Date(),
            id: Date.now() + Math.random()
        });
        
        // 최근 20개만 유지
        if (state.systemAlerts.length > 20) {
            state.systemAlerts = state.systemAlerts.slice(0, 20);
        }
    },
    
    UPDATE_REAL_TIME_DATA(state, { key, data }) {
        state.realTimeData = {
            ...state.realTimeData,
            [key]: {
                ...data,
                lastUpdated: new Date()
            }
        };
    },
    
    CLEAR_MESSAGES(state) {
        state.messages = [];
    },
    
    CLEAR_NOTIFICATIONS(state) {
        state.notifications = [];
    },
    
    RESET_SSE_STATE(state) {
        if (state.connection) {
            state.connection.close();
            state.connection = null;
        }
        
        // Heartbeat interval 정리
        if (state.heartbeatInterval) {
            clearInterval(state.heartbeatInterval);
            state.heartbeatInterval = null;
        }
        
        state.isConnected = false;
        state.isConnecting = false;
        state.messages = [];
        state.notifications = [];
        state.systemAlerts = [];
        state.realTimeData = {};
        state.error = null;
        state.initialLoadCompleted = false;
        state.isLoadingNotifications = false;
        state.reconnectAttempts = 0;
        state.connectionInfo.lastHeartbeat = null;
    },
    
    SET_CONNECTION(state, connection) {
        state.connection = connection;
    },
    
    SET_INITIAL_LOAD_COMPLETED(state, completed) {
        state.initialLoadCompleted = completed;
    },
    
    SET_LOADING_NOTIFICATIONS(state, loading) {
        state.isLoadingNotifications = loading;
    },
    
    // Heartbeat 관련 mutations
    SET_LAST_HEARTBEAT(state, timestamp) {
        state.connectionInfo.lastHeartbeat = timestamp;
    },
    
    SET_HEARTBEAT_INTERVAL(state, interval) {
        state.heartbeatInterval = interval;
    },
    
    CLEAR_HEARTBEAT_INTERVAL(state) {
        if (state.heartbeatInterval) {
            clearInterval(state.heartbeatInterval);
            state.heartbeatInterval = null;
        }
    },
    
    SET_RECONNECT_ATTEMPTS(state, attempts) {
        state.reconnectAttempts = attempts;
    },
    
    RESET_RECONNECT_ATTEMPTS(state) {
        state.reconnectAttempts = 0;
    }
};

const actions = {
    // 알림 서비스 시작 (SSE 연결)
    async connect({ commit, dispatch, state, getters, rootState }) {
        // 🚫 SSE 기능 임시 비활성화 (true로 변경하면 활성화)
        const sseDisabled = false;
        if (sseDisabled) {
            console.log('⚠️ SSE 기능이 비활성화되어 있습니다.');
            commit('SET_CONNECTED', false);
            commit('SET_CONNECTING', false);
            commit('SET_INITIAL_LOAD_COMPLETED', true);
            return;
        }
        
        console.log('🔗 Starting SSE connection...');
        console.log(`🔍 Current connection status: ${getters.connectionStatus}`);
        
        // 🔧 연결 가능 여부 체크
        if (!getters.canConnect) {
            console.log(`⚠️ Cannot connect - Status: ${getters.connectionStatus}`);
            return;
        }
        
        // 🔧 기존 연결 완전 정리 (중요!)
        if (state.connection) {
            console.log('🧹 Cleaning up existing connection...');
            try {
                state.connection.close();
            } catch (e) {
                console.log('⚠️ Error closing existing connection:', e);
            }
            commit('SET_CONNECTION', null);
        }
        
        // 🔧 연결 시도 전 잠시 대기 (서버/브라우저 정리 시간)
        await new Promise(resolve => setTimeout(resolve, 500));
        
        commit('SET_CONNECTING', true);
        commit('SET_ERROR', null);
        
        try {
            // 1. 먼저 기존 미확인 알림 로드 (한 번만)
            if (!state.initialLoadCompleted) {
                console.log('📡 Loading initial notifications...');
                await dispatch('loadNotifications');
                // 🔧 알림 로드 후 잠시 대기
                await new Promise(resolve => setTimeout(resolve, 300));
            }
            
            // 2. SSE 연결 시작
            const baseUrl = process.env.VUE_APP_API_URL || '';
            let sseUrl = `${baseUrl}/esupport-api/common/sse/connect?channel=ezone`;
            
            console.log('🌐 Creating new EventSource:', sseUrl);
    
            const eventSource = new EventSource(sseUrl, {
                withCredentials: true
            });
            
            commit('SET_CONNECTION', eventSource);
            
            // SSE 연결 성공
            eventSource.onopen = () => {
                console.log('✅ SSE connection opened successfully');
                console.log('🔍 EventSource readyState:', eventSource.readyState);
                console.log('🔍 EventSource URL:', eventSource.url);
                commit('SET_CONNECTED', true);
                commit('SET_CONNECTING', false);
                commit('RESET_RECONNECT_ATTEMPTS');
                
                // 🔧 초기 heartbeat 시간만 설정
                commit('SET_LAST_HEARTBEAT', new Date());
            };
            
            // 일반 메시지 수신 (heartbeat 포함)
            eventSource.onmessage = (event) => {
                console.log('📨 SSE message received:', event.data);
                
                try {
                    const data = JSON.parse(event.data);
                    
                    // Heartbeat 메시지 처리
                    if (data.type === 'heartbeat' || data.type === 'ping') {
                        console.log('💗 Heartbeat received from server');
                        commit('SET_LAST_HEARTBEAT', new Date());
                        return;
                    }
                    
                    // 일반 메시지 처리
                    commit('ADD_MESSAGE', data);
                } catch (error) {
                    console.log('💗 Simple heartbeat received (non-JSON)');
                    commit('SET_LAST_HEARTBEAT', new Date());
                }
            };
            
            // 브라우저 Visibility API를 활용한 효율적 연결 체크
            const handleVisibilityChange = () => {
                // 탭이 다시 활성화되었을 때만 연결 상태 체크
                if (!document.hidden) {
                    const now = new Date();
                    const lastHeartbeat = state.connectionInfo.lastHeartbeat;
                    
                    if (lastHeartbeat) {
                        const timeSinceLastHeartbeat = now - new Date(lastHeartbeat);
                        
                        // 5분 이상 heartbeat가 없으면 재연결 시도
                        if (timeSinceLastHeartbeat > 300000) { // 5분 = 300초
                            console.warn('⚠️ Long heartbeat gap detected after tab activation, checking connection...');
                            dispatch('attemptReconnect');
                        } else {
                            console.log(`💗 Connection seems healthy - last heartbeat ${Math.round(timeSinceLastHeartbeat/1000)}s ago`);
                        }
                    }
                }
            };
            
            // Visibility 이벤트 리스너 등록
            document.addEventListener('visibilitychange', handleVisibilityChange);
            
            // API 새로고침 이벤트 리스너 등록
            const handleRefreshNotifications = async (event) => {
                console.log('🔄 ✅ Received refresh-notifications-from-api event:', event.detail);
                console.log('🔄 About to call loadNotifications action...');
                try {
                    console.log('📡 Calling dispatch(loadNotifications)...');
                    await dispatch('loadNotifications');
                    console.log('✅ loadNotifications completed successfully');
                    console.log('📊 Current notifications count:', state.notifications.length);
                    
                    // 새로고침 후 글로벌 이벤트 발생 (UI 업데이트용)
                    setTimeout(() => {
                        const unreadCount = state.notifications.filter(n => !n.viewYn || n.viewYn === 'N').length;
                        console.log('🔄 Dispatching new-notification-received event with count:', unreadCount);
                        
                        window.dispatchEvent(new CustomEvent('new-notification-received', {
                            detail: {
                                reason: 'API refresh completed',
                                count: unreadCount,
                                totalNotifications: state.notifications.length
                            }
                        }));
                        console.log('✅ new-notification-received event dispatched after API refresh');
                    }, 200);
                } catch (error) {
                    console.error('❌ Failed to refresh notifications from API:', error);
                    console.error('❌ Error details:', error.message, error.stack);
                }
            };
            
            console.log('🔧 Registering refresh-notifications-from-api event listener...');
            window.addEventListener('refresh-notifications-from-api', handleRefreshNotifications);
            console.log('✅ Event listener registered successfully');
            
            // 연결이 정리될 때 이벤트 리스너도 정리
            eventSource.addEventListener('close', () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('refresh-notifications-from-api', handleRefreshNotifications);
            });
            
            // 🔔 알림 메시지 수신
            eventSource.addEventListener('notification', (event) => {
                console.log('🔔 SSE: Raw notification received:', event.data);
                try {
                    const notificationData = JSON.parse(event.data);
                    console.log('🔔 SSE: Parsed notification data:', notificationData);
                    dispatch('handleNewNotification', notificationData);
                    console.log('🔔 SSE: handleNewNotification dispatched');
                } catch (error) {
                    console.error('❌ SSE: Failed to parse notification data:', error, event.data);
                }
            });
            
            // 📧 일반 메시지 수신 (알림이 아닌 다른 메시지)
            eventSource.addEventListener('message', (event) => {
                console.log('📧 SSE: Raw message received:', event.data);
                try {
                    const messageData = JSON.parse(event.data);
                    console.log('📧 SSE: Parsed message data:', messageData);
                    
                    // 메시지 타입에 따른 분기 처리
                    switch (messageData.type) {
                        case 'notification':
                        case 'alarm':
                            console.log('📧 SSE: Treating as notification');
                            dispatch('handleNewNotification', messageData);
                            break;
                        case 'update':
                            console.log('📧 SSE: Treating as data update');
                            dispatch('handleDataUpdate', messageData);
                            break;
                        case 'system':
                            console.log('📧 SSE: Treating as system message');
                            dispatch('handleSystemMessage', messageData);
                            break;
                        default:
                            console.log('📝 SSE: General message:', messageData);
                            commit('ADD_MESSAGE', messageData);
                    }
                } catch (error) {
                    console.error('❌ SSE: Failed to parse message data:', error, event.data);
                    console.log('📝 SSE: Treating as plain text message');
                    // JSON이 아닌 텍스트 메시지일 수 있음
                    commit('ADD_MESSAGE', {
                        id: Date.now(),
                        type: 'text',
                        content: event.data,
                        timestamp: new Date()
                    });
                }
            });
            
            // 🔄 실시간 데이터 업데이트 수신
            eventSource.addEventListener('data-update', (event) => {
                console.log('🔄 Data update received:', event.data);
                try {
                    const updateData = JSON.parse(event.data);
                    commit('UPDATE_REAL_TIME_DATA', {
                        key: updateData.key || 'default',
                        data: updateData
                    });
                } catch (error) {
                    console.error('❌ Failed to parse data update:', error, event.data);
                }
            });
            
            // ⚠️ 시스템 알림 수신
            eventSource.addEventListener('system-alert', (event) => {
                console.log('⚠️ System alert received:', event.data);
                try {
                    const alertData = JSON.parse(event.data);
                    commit('ADD_SYSTEM_ALERT', alertData);
                    
                    // 시스템 알림은 즉시 토스트로 표시
                    if (rootState && rootState.dispatch) {
                        rootState.dispatch('common/toast', {
                            type: alertData.severity || 'warning',
                            title: `⚠️ 시스템 알림`,
                            message: alertData.message || '시스템 메시지가 도착했습니다.',
                            duration: 8000
                        });
                    }
                } catch (error) {
                    console.error('❌ Failed to parse system alert:', error, event.data);
                }
            });
            
            // 연결 에러 처리
            eventSource.onerror = (event) => {
                console.log('⚠️ SSE connection error:', event);
                console.log('🔍 Error EventSource readyState:', eventSource.readyState);
                console.log('🔍 Error details:', {
                    type: event.type,
                    target: event.target?.url,
                    readyState: eventSource.readyState,
                    withCredentials: eventSource.withCredentials
                });
                
                commit('SET_CONNECTED', false);
                commit('SET_CONNECTING', false);
                
                // 🔧 에러 유형 분석
                const readyState = eventSource.readyState;
                let errorMessage = 'Connection error';
                
                if (readyState === EventSource.CONNECTING) {
                    errorMessage = 'Connection failed - server may be unavailable';
                } else if (readyState === EventSource.CLOSED) {
                    errorMessage = 'Connection closed by server';
                }
                
                console.log(`🔍 EventSource readyState: ${readyState}`);
                
                // 재연결 시도 (점진적 지연)
                if (state.reconnectAttempts < state.maxReconnectAttempts) {
                    dispatch('attemptReconnect');
                } else {
                    console.error('❌ Max reconnection attempts reached');
                    commit('SET_ERROR', errorMessage + ' - Max attempts reached');
                }
            };
            
            console.log('✅ SSE connection setup completed');
            
        } catch (error) {
            console.error('❌ Failed to start SSE connection:', error);
            commit('SET_ERROR', error.message);
            commit('SET_CONNECTING', false);
        }
    },
    
    // 기존 미확인 알림 초기 로드 (앱 시작 시 한 번만)
    async loadNotifications({ commit, state }) {

        
        if (state.initialLoadCompleted) {
            console.log('⚠️ Initial notifications already loaded, skipping...');
            return;
        }
        
        if (state.isConnecting) {
            console.log('⚠️ Already connecting, skipping initial load...');
            return;
        }
        
        if (state.isLoadingNotifications) {
            console.log('⚠️ Already loading notifications, skipping...');
            return;
        }
        
        console.log('📡 Starting initial notification load...');
        commit('SET_LOADING_NOTIFICATIONS', true);
        
        try {
            // 기존 미확인 알림 조회
            RequsetApI.selectMtAlarmEventUnread({
               
            }, (response) => {
                console.log('📨 Initial notifications response:', response.data);
                
                if (response.data && response.data.status === 'ok') {
                    const notifications = response.data.results || [];
                    console.log('🔔 Initial notifications loaded:', notifications.length);
                    
                    commit('SET_NOTIFICATIONS', notifications);
                    commit('SET_INITIAL_LOAD_COMPLETED', true);
                    console.log('✅ Initial notifications load completed');
                    
                } else {
                    console.error('❌ Failed to load initial notifications:', response.data?.message);
                    commit('SET_NOTIFICATIONS', []);
                    commit('SET_INITIAL_LOAD_COMPLETED', true);
                }
                
                commit('SET_LOADING_NOTIFICATIONS', false);
            });
        } catch (error) {
            console.error('❌ Error loading initial notifications:', error);
            commit('SET_NOTIFICATIONS', []);
            commit('SET_INITIAL_LOAD_COMPLETED', true);
            commit('SET_LOADING_NOTIFICATIONS', false);
        }
    },
    
    // SSE 연결 해제
    disconnect({ commit, state }) {
        console.log('🔌 Disconnecting SSE...');
        
        // Heartbeat interval 정리
        commit('CLEAR_HEARTBEAT_INTERVAL');
        
        if (state.connection) {
            state.connection.close();
            commit('SET_CONNECTION', null);
        }
        
        commit('SET_CONNECTED', false);
        console.log('✅ SSE disconnected');
    },
    
    // 알림 읽음 처리
    async markNotificationRead({ commit }, notificationId) {

        try {
            RequsetApI.updateMtAlarmEventRead({
                eventSeq: notificationId,
                //userId: userId
            }, (response) => {
                if (response.data && response.data.status === 'ok') {
                    commit('MARK_NOTIFICATION_READ', notificationId);
                    console.log('✅ Notification marked as read:', notificationId);
                    
                    // 읽음 처리 완료 시 전역 이벤트 발생 (SSENotificationUnit 카운트 업데이트용)
                    setTimeout(() => {
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('notification-read-updated', {
                                detail: { notificationId, type: 'read' }
                            }));
                        }
                    }, 100);
                } else {
                    console.error('Failed to mark notification as read:', response.data?.message);
                }
            });
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    },
    
    // 모든 알림 읽음 처리
    async markAllNotificationsRead({ commit }) {

        try {
            RequsetApI.updateMtAlarmEventAllRead({
            }, (response) => {
                if (response.data && response.data.status === 'ok') {
                    commit('MARK_ALL_NOTIFICATIONS_READ');
                    console.log('✅ All notifications marked as read');
                } else {
                    console.error('Failed to mark all notifications as read:', response.data?.message);
                }
            });
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    },
    
    // 알림 삭제
    async deleteNotification({ commit }, notificationId) {

        try {
            RequsetApI.deleteMtAlarmEvent({
                eventSeq: notificationId,

            }, (response) => {
                if (response.data && response.data.status === 'ok') {
                    commit('REMOVE_NOTIFICATION', notificationId);
                    console.log('✅ Notification deleted:', notificationId);
                } else {
                    console.error('Failed to delete notification:', response.data?.message);
                }
            });
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    },
    
    // 읽은 알림 삭제
    async deleteReadNotifications({ commit, state }) {
        try {
            RequsetApI.deleteReadMtAlarmEvent({}, (response) => {
                if (response.data && response.data.status === 'ok') {
                    // 읽은 알림들을 store에서 제거
                    const readNotifications = state.notifications.filter(n => n.read);
                    readNotifications.forEach(notification => {
                        commit('REMOVE_NOTIFICATION', notification.id);
                    });
                    console.log('✅ Read notifications deleted');
                } else {
                    console.error('Failed to delete read notifications:', response.data?.message);
                }
            });
        } catch (error) {
            console.error('Error deleting read notifications:', error);
        }
    },

    // 모든 알림 삭제
    async clearNotifications({ commit, state }) {

        try {
            // 현재 알림 목록에서 각각 삭제 처리
            const notifications = state.notifications.slice(); // 복사본 생성

            if (notifications.length === 0) {
                console.log('✅ No notifications to clear');
                return;
            }
            
            // 순차적으로 개별 삭제 처리
            for (const notification of notifications) {
                await new Promise((resolve) => {
                    RequsetApI.deleteMtAlarmEvent({
                        eventSeq: notification.id
                    }, (response) => {
                        if (response.data && response.data.status === 'ok') {
                            commit('REMOVE_NOTIFICATION', notification.id);
                            console.log('✅ Notification deleted:', notification.id);
                        } else {
                            console.error('Failed to delete notification:', notification.id, response.data?.message);
                        }
                        resolve();
                    });
                });
            }
            
            console.log('✅ All notifications cleared');
            
        } catch (error) {
            console.error('Error clearing all notifications:', error);
        }
    },
    
    clearMessages({ commit }) {
        commit('CLEAR_MESSAGES');
    },
    
    reset({ commit }) {
        commit('RESET_SSE_STATE');
    },
    
    async loadInitialNotifications({ dispatch }) {
        await dispatch('loadNotifications');
    },
    
    // SSE로 받은 새 알림 처리 (강화된 버전)
    async handleNewNotification({ commit, rootState, dispatch }, notification) {
        console.log('🆕 SSE Store: Processing new notification:', notification);
        console.log('🆕 Notification structure:', {
            eventSeq: notification.data.eventSeq,
            alarmNm: notification.data.alarmNm,
            eventCont: notification.data.eventCont,
            readYn: notification.data.readYn
        });

    
                // 먼저 DB에서 완전한 알림 데이터를 조회
        if (notification.data.eventSeq) {
            try {
                // 새로 생성된 알림의 완전한 정보를 DB에서 가져오기
                RequsetApI.selectMtAlarmEvent({ eventSeq: notification.data.eventSeq }, (response) => {
                    console.log('📡 DB response for notification:', response.data);
                    if (response.data && response.data.status === 'ok' && response.data.results.length > 0) {
                        const fullNotification = response.data.results[0];
                        console.log('📡 Full notification data from DB:', fullNotification);
                        console.log('📡 About to commit ADD_NOTIFICATION with full data');
  
                        // DB에서 가져온 완전한 데이터로 store에 추가
                        commit('ADD_NOTIFICATION', fullNotification);
                        console.log('📡 ADD_NOTIFICATION committed with full data');
                    } else {
                        // DB 조회 실패 시 원본 데이터 사용
                        console.warn('⚠️ Failed to fetch full notification, using original data');
                        console.warn('⚠️ Response:', response.data);
                        console.log('⚠️ About to commit ADD_NOTIFICATION with original data');
                        commit('ADD_NOTIFICATION', notification.data);
                        console.log('⚠️ ADD_NOTIFICATION committed with original data');
                    }
                });
            } catch (error) {
                console.error('❌ Error fetching full notification:', error);
                // 에러 시 원본 데이터 사용
                commit('ADD_NOTIFICATION', notification.data);
            }
        } else {
            // eventSeq가 없으면 원본 데이터 사용
            commit('ADD_NOTIFICATION', notification.data);
        }
        
        // 사용자명으로 메시지 포맷팅
        const formatAlarmMessage = (message, userNm) => {
            if (!message) return '';
            return message.replace('{0}', userNm || '사용자');
        };
        
        const formattedMessage = formatAlarmMessage(notification.alarmDesc, notification.userNm);
        const notificationTitle = notification.title || '새 알림';
        const notificationBody = notification.eventCont || formattedMessage || notification.message || '새로운 알림이 도착했습니다.';
        
        if (rootState && rootState.dispatch) {
            try {
                console.log('🔔 Calling rootState.dispatch for toast...');
                await rootState.dispatch('common/toast', {
                    type: 'info',
                    title: `📢 ${notificationTitle}`,
                    message: `${formattedMessage ? `[${notificationTitle}] ${formattedMessage}` : notificationBody}`,
                    duration: 5000 // 5초간 표시
                });
                console.log('✅ Toast dispatch successful');
            } catch (error) {
                console.warn('⚠️ Toast dispatch failed, using fallback:', error);
                // 토스트 실패 시 콘솔 로그로 대체
                console.log(`📢 새 알림: [${notificationTitle}] ${notificationBody}`);
            }
        } else {
            console.warn('⚠️ rootState or rootState.dispatch not available for toast');
        }
        
        // 🔔 브라우저 알림 표시 (더 풍부한 옵션)
        console.log('🔔 Browser notification permission:', Notification.permission);
        
        if (Notification.permission === 'granted') {
            try {
                console.log('🔔 Creating browser notification:', {
                    title: notificationTitle,
                    body: notificationBody,
                    tag: `notification-${notification.eventSeq || notification.id}`
                });
                
                const browserNotification = new Notification(notificationTitle, {
                    body: notificationBody,
                    icon: '/favicon.ico',
                    badge: '/favicon.ico',
                    tag: `notification-${notification.eventSeq || notification.id}`, // 중복 방지
                    requireInteraction: true, // 사용자가 직접 닫을 때까지 유지
                    timestamp: Date.now(),
                    data: {
                        notificationId: notification.eventSeq || notification.id,
                        type: notification.alarmType || 'info',
                        timestamp: notification.creDat
                    }
                });
                
                console.log('✅ Browser notification created successfully');
                
                // 알림 클릭 시 처리
                browserNotification.onclick = () => {
                    console.log('🖱️ Browser notification clicked:', notification);
                    
                    // 브라우저 포커스
                    window.focus();
                    
                    // 알림을 읽음 처리
                    dispatch('markNotificationRead', notification.eventSeq || notification.id);
                    
                    // 알림 닫기
                    browserNotification.close();
                };
                
                // 자동 닫기 (10초 후)
                setTimeout(() => {
                    console.log('🔔 Auto-closing browser notification');
                    browserNotification.close();
                }, 10000);
                
            } catch (error) {
                console.error('❌ Failed to show browser notification:', error);
            }
        } else if (Notification.permission === 'default') {
            // 권한이 없으면 권한 요청
            console.log('🔔 Requesting notification permission...');
            const permission = await Notification.requestPermission();
            console.log('🔔 Notification permission result:', permission);
        } else {
            console.warn('⚠️ Browser notification permission denied or not supported');
        }
        
        // 🔊 알림 사운드 재생 (선택사항)
        console.log('🔊 Attempting to play notification sound...');
        try {
            // 간단한 알림 소리 (Web Audio API 사용)
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
            
            gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
            
            console.log('✅ Notification sound played successfully');
        } catch (error) {
            console.log('🔇 Audio notification not available:', error.message);
        }
        
        // 📊 알림 통계 업데이트 (필요시)
        console.log('📊 Notification stats:', {
            total: rootState?.sse?.notifications?.length || 0,
            unread: rootState?.sse?.notifications?.filter(n => !n.read)?.length || 0,
            type: notification.alarmType || 'unknown'
        });
        
        console.log('✅ Enhanced notification processing completed');
    },
    
    // 📊 데이터 업데이트 메시지 처리
    async handleDataUpdate({ commit, rootState }, updateData) {
        console.log('📊 Processing data update:', updateData);
        
        // 실시간 데이터 스토어에 업데이트
        commit('UPDATE_REAL_TIME_DATA', {
            key: updateData.key || updateData.type || 'default',
            data: {
                ...updateData,
                lastUpdated: new Date(),
                timestamp: updateData.timestamp || Date.now()
            }
        });
        
        // 특정 데이터 업데이트의 경우 토스트 알림 표시
        if (updateData.showNotification) {
            try {
                await rootState.dispatch('common/toast', {
                    type: 'success',
                    title: '📊 데이터 업데이트',
                    message: updateData.message || '데이터가 업데이트되었습니다.',
                    duration: 3000
                });
            } catch (error) {
                console.warn('⚠️ Data update toast failed:', error);
            }
        }
        
        console.log('✅ Data update processed');
    },
    
    // 🔧 시스템 메시지 처리
    async handleSystemMessage({ commit, rootState }, systemData) {
        console.log('🔧 Processing system message:', systemData);
        
        // 시스템 알림으로 추가
        commit('ADD_SYSTEM_ALERT', {
            ...systemData,
            id: systemData.id || Date.now(),
            timestamp: new Date(),
            type: 'system'
        });
        
        // 중요도에 따른 알림 처리
        const severity = systemData.severity || systemData.level || 'info';
        const icons = {
            'critical': '🚨',
            'error': '❌', 
            'warning': '⚠️',
            'info': 'ℹ️',
            'success': '✅'
        };
        
        const icon = icons[severity] || 'ℹ️';
        
        try {
            await rootState.dispatch('common/toast', {
                type: severity === 'critical' ? 'error' : severity,
                title: `${icon} 시스템 메시지`,
                message: systemData.message || '시스템 메시지가 도착했습니다.',
                duration: severity === 'critical' ? 0 : 6000 // critical은 수동으로 닫을 때까지
            });
        } catch (error) {
            console.warn('⚠️ System message toast failed:', error);
        }
        
        // 브라우저 알림도 표시 (중요한 시스템 메시지의 경우)
        if (severity === 'critical' || severity === 'error') {
            if (Notification.permission === 'granted') {
                try {
                    new Notification(`${icon} 시스템 알림`, {
                        body: systemData.message || '중요한 시스템 메시지가 있습니다.',
                        icon: '/favicon.ico',
                        requireInteraction: true,
                        tag: `system-${systemData.id || Date.now()}`
                    });
                } catch (error) {
                    console.error('❌ Failed to show system browser notification:', error);
                }
            }
        }
        
        console.log('✅ System message processed');
    },
    
    // 재연결 시도
    async attemptReconnect({ commit, dispatch, state }) {
        if (state.isConnecting) {
            console.log('⚠️ Already attempting to reconnect...');
            return;
        }
        
        const attempt = state.reconnectAttempts + 1;
        commit('SET_RECONNECT_ATTEMPTS', attempt);
        
        // 🔧 점진적 지연 계산 (1초, 3초, 6초, 10초, 15초)
        const delays = [1000, 3000, 6000, 10000, 15000];
        const delay = delays[attempt - 1] || 15000;
        
        console.log(`🔄 Attempting reconnection ${attempt}/${state.maxReconnectAttempts} in ${delay/1000}s...`);
        
        // 🔧 연결 완전 정리
        if (state.connection) {
            try {
                state.connection.close();
            } catch (e) {
                console.log('⚠️ Error closing connection during reconnect:', e);
            }
            commit('SET_CONNECTION', null);
        }
        
        // 상태 정리
        commit('SET_CONNECTED', false);
        commit('SET_CONNECTING', false);
        
        // 🔧 점진적 지연 후 재연결
        setTimeout(async () => {
            console.log(`🔄 Starting reconnection attempt ${attempt}...`);
            await dispatch('connect');
        }, delay);
    },
    
    // 수동 재연결
    async reconnect({ commit, dispatch, state }) {
        console.log('🔄 Manual reconnection requested...');
        
        // 🔧 재연결 시도 횟수 초기화
        commit('RESET_RECONNECT_ATTEMPTS');
        
        // 🔧 기존 연결 완전 정리
        if (state.connection) {
            try {
                state.connection.close();
            } catch (e) {
                console.log('⚠️ Error closing connection during manual reconnect:', e);
            }
            commit('SET_CONNECTION', null);
        }
        
        commit('SET_CONNECTED', false);
        commit('SET_CONNECTING', false);
        
        // 잠시 대기 후 연결
        setTimeout(async () => {
            await dispatch('connect');
        }, 1000);
    },
    
    // 🔧 디버깅용: 현재 연결 상태 상세 확인
    checkConnectionStatus({ state, getters }) {
        console.log('🔍 === SSE Connection Status Check ===');
        console.log('🔍 canConnect:', getters.canConnect);
        console.log('🔍 isConnected:', getters.isConnected);
        console.log('🔍 isConnecting:', getters.isConnecting);
        console.log('🔍 connectionStatus:', getters.connectionStatus);
        console.log('🔍 reconnectAttempts:', state.reconnectAttempts);
        console.log('🔍 lastHeartbeat:', state.connectionInfo.lastHeartbeat);
        console.log('🔍 error:', state.error);
        
        if (state.connection) {
            console.log('🔍 EventSource readyState:', state.connection.readyState);
            console.log('🔍 EventSource URL:', state.connection.url);
            console.log('🔍 EventSource details:', {
                readyState: state.connection.readyState,
                url: state.connection.url,
                withCredentials: state.connection.withCredentials
            });
            
            // EventSource readyState 설명
            const readyStates = {
                0: 'CONNECTING',
                1: 'OPEN', 
                2: 'CLOSED'
            };
            console.log(`🔍 ReadyState meaning: ${state.connection.readyState} = ${readyStates[state.connection.readyState]}`);
        } else {
            console.log('🔍 No EventSource connection object');
        }
        console.log('🔍 === End Status Check ===');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}; 